import { type AppScenario, picker, section, tab, table } from './build.ts';

const DEALS = ['Quartz Énergie', 'Hélix Santé', 'Boréal Data', 'Ponant Services', 'Sillage Industrie', 'Archipel Conseil', 'Talus Logiciels', 'Méridien Mobilité'];
const STAGES = ['Découverte', 'Démo', 'Proposition', 'Négociation'];
const OWNERS = ['Camille', 'Sacha', 'Noa', 'Alex'];

const pipelineRows = () => {
  const p = picker(11);
  return DEALS.map((deal) => {
    const amount = p.int(8, 120) * 1000;
    const days = p.int(3, 60);
    return {
      deal,
      owner: p.pick(OWNERS),
      stage: p.pick(STAGES),
      amount: `${amount.toLocaleString('fr-FR')} €`,
      close: `${p.int(10, 31)}/${p.pick(['10', '11', '12'])}/2026`,
      risk: days > 30 ? `Aucune activité depuis ${days} jours` : days > 14 ? 'Décideur non identifié' : null,
      next: days > 30 ? 'Relancer le sponsor cette semaine' : 'Préparer la proposition',
    };
  });
};

export const managerScenarios: AppScenario[] = [
  {
    id: 'pipeline-review',
    title: 'Revue de pipeline (manager)',
    job: 'La manager, le vendredi, décide sur quelles affaires aider son équipe la semaine suivante.',
    stresses: ['chiffres avec tendance', 'tableau d’affaires avec montants et dates', 'valeurs manquantes (risque absent)'],
    wished: ['courbe du pipeline sur 8 semaines', 'répartition par étape (barres)', 'filtre par commercial', 'chiffres et tableau côte à côte'],
    payload: {
      status: 'complete',
      tabs: [
        tab('overview', 'Vue d’ensemble', [
          section('kpis', 'Les chiffres de la semaine', 'summary', 'metric_grid', {
            metrics: [
              { label: 'Pipeline pondéré', value: '412 000', unit: '€', trend: 'up', trend_value: '+6 %', status: 'good' },
              { label: 'Affaires actives', value: 34, trend: 'stable' },
              { label: 'Taux de transformation', value: '22', unit: '%', trend: 'down', trend_value: '−3 pts', status: 'warning' },
              { label: 'Affaires sans activité > 30 j', value: 5, status: 'critical' },
              { label: 'Cycle moyen', value: 47, unit: 'jours', trend: 'up', trend_value: '+4 j', status: 'warning' },
              { label: 'Nouvelles affaires', value: 9, trend: 'up', trend_value: '+3' },
            ],
          }),
          section('trend', 'Pipeline pondéré sur 8 semaines', 'detail', 'chart', {
            kind: 'line', unit: 'k€',
            categories: ['S32', 'S33', 'S34', 'S35', 'S36', 'S37', 'S38', 'S39'],
            series: [
              { key: 'weighted', label: 'Pipeline pondéré', values: [318, 335, 342, 361, 355, 380, 389, 412] },
              { key: 'won', label: 'Signé cumulé', values: [40, 52, 52, 70, 88, 88, 104, 121] },
            ],
            target: { label: 'Objectif du trimestre', value: 450 },
            insight: 'Le pipeline progresse chaque semaine mais reste sous l’objectif : il manque 38 k€ pondérés.',
          }, 'half'),
          section('by-stage', 'Affaires par étape et par commercial', 'detail', 'chart', {
            kind: 'stacked_bar', unit: 'affaires',
            categories: ['Découverte', 'Démo', 'Proposition', 'Négociation'],
            series: OWNERS.map((owner, index) => ({ key: owner, label: owner, values: [4 - (index % 2), 3, 2 + (index % 3), index === 0 ? 2 : 1] })),
            insight: 'Les affaires s’accumulent en découverte ; peu passent en négociation.',
          }, 'half'),
          section('at-risk', 'Affaires à risque', 'detail', 'data_table', table('Affaires à risque', [
            ['deal', 'Affaire'], ['owner', 'Commercial'], ['stage', 'Étape'], ['amount', 'Montant'], ['close', 'Clôture prévue'], ['risk', 'Risque'], ['next', 'Action proposée'],
          ], pipelineRows())),
        ]),
        tab('team', 'Équipe', [
          section('by-rep', 'Par commercial', 'detail', 'data_table', table('Par commercial', [
            ['rep', 'Commercial'], ['deals', 'Affaires'], ['weighted', 'Pondéré'], ['won', 'Gagnées (trim.)'], ['activity', 'Activités (7 j)'],
          ], OWNERS.map((rep, index) => ({ rep, deals: 6 + index * 3, weighted: `${(80 + index * 37) * 1000} €`, won: 2 + index, activity: 31 - index * 4 })))),
        ], 'team'),
      ],
    },
  },
  {
    id: 'account-health',
    title: 'Santé d’un compte client',
    job: 'Le responsable de compte, avant le renouvellement, veut savoir où en est la relation.',
    stresses: ['jauge à dimensions', 'courbe de santé', 'actions proposées', 'chronologie avec événements à venir'],
    wished: ['comparaison avec les autres comptes'],
    payload: {
      status: 'complete',
      tabs: [tab('result', 'Santé', [
        section('gauge', 'Santé du compte', 'summary', 'health_gauge', {
          entity_name: 'Hélix Santé', entity_type: 'account', overall_score: 62, overall_max: 100, overall_status: 'at_risk',
          dimensions: [
            { label: 'Usage', score: 18, max: 30, weight: 0.3, signals: ['Usage hebdomadaire en baisse de 20 %', '3 utilisateurs inactifs'] },
            { label: 'Relation', score: 22, max: 25, weight: 0.25, signals: ['Sponsor actif', 'Réunion trimestrielle tenue'] },
            { label: 'Support', score: 12, max: 20, weight: 0.2, signals: ['2 tickets ouverts depuis plus de 10 jours'] },
            { label: 'Commercial', score: 10, max: 25, weight: 0.25, signals: ['Renouvellement dans 60 jours', 'Pas de discussion d’extension'] },
          ],
          last_updated: '2026-10-02',
          recommended_action: 'Organiser un point d’usage avec les 3 utilisateurs inactifs avant de parler renouvellement.',
        }, 'half'),
        section('health-trend', 'Santé sur 6 mois', 'detail', 'chart', {
          kind: 'line', unit: '/100',
          categories: ['mai', 'juin', 'juil.', 'août', 'sept.', 'oct.'],
          series: [{ key: 'health', label: 'Santé du compte', values: [78, 81, 76, 70, 65, 62] }],
          target: { label: 'Seuil d’alerte', value: 65 },
          insight: 'La santé baisse depuis juillet et passe sous le seuil d’alerte ce mois-ci.',
        }, 'half'),
        section('actions', 'Ce que je propose', 'actions', 'action_list', {
          title: 'Ce que je propose',
          items: [
            { id: 'a1', label: 'Point d’usage avec les utilisateurs inactifs', priority: 'high', status: 'pending', due: '2026-10-09' },
            { id: 'a2', label: 'Faire fermer les 2 tickets anciens', priority: 'medium', status: 'in_progress' },
            { id: 'a3', label: 'Préparer la proposition de renouvellement', priority: 'medium', status: 'pending', due: '2026-11-01' },
          ],
        }, 'half'),
        section('history', 'Ce qui s’est passé', 'context', 'timeline', {
          title: 'Ce qui s’est passé',
          insight: 'La relation reste bonne avec le sponsor, mais l’usage décroche depuis l’été.',
          events: [
            { date: '2026-12-01', label: 'Comité de renouvellement', kind: 'Rendez-vous' },
            { date: '2026-10-09', label: 'Point d’usage avec les utilisateurs inactifs', kind: 'Rendez-vous' },
            { date: '2026-09-30', label: 'Deux tickets toujours ouverts après 10 jours', kind: 'Support', tone: 'warning' },
            { date: '2026-09-18', label: 'Réunion trimestrielle avec le sponsor', kind: 'Rendez-vous', tone: 'good', detail: 'Le sponsor confirme l’intérêt pour l’extension à l’équipe marketing, sous réserve que l’usage remonte.' },
            { date: '2026-08-26', label: 'Usage hebdomadaire en baisse de 20 %', kind: 'Usage', tone: 'critical' },
            { date: '2026-07-04', label: 'Trois utilisateurs ne se connectent plus', kind: 'Usage', tone: 'warning' },
            { date: '2026-06-12T14:30:00', label: 'Formation des nouveaux utilisateurs', kind: 'Rendez-vous', tone: 'good' },
          ],
        }, 'half'),
      ])],
    },
  },
  {
    id: 'team-dashboard',
    title: 'Tableau de bord d’équipe (mini-app)',
    job: 'L’équipe, chaque matin, voit où elle en est et qui a besoin d’aide.',
    stresses: ['plusieurs onglets', 'métriques nombreuses', 'onglet équipe'],
    wished: ['graphique d’activité par jour', 'objectif vs réalisé en barre de progression', 'classement', 'cartes côte à côte en grille'],
    payload: {
      status: 'complete',
      tabs: [
        tab('me', 'Moi', [
          section('my-kpis', 'Ma semaine', 'summary', 'metric_grid', {
            metrics: [
              { label: 'Rendez-vous', value: 7, unit: '/ 10 visés', status: 'warning' },
              { label: 'Appels', value: 64, trend: 'up', trend_value: '+12' },
              { label: 'Propositions envoyées', value: 3 },
              { label: 'Signées', value: 1, status: 'good' },
            ],
          }, 'half'),
          section('my-score', 'Mon objectif du trimestre', 'summary', 'score_card', {
            title: 'Objectif du trimestre', status: 'warning', score: 64, score_label: '% atteint',
            insight: 'Il reste 36 000 € à signer en 6 semaines : deux affaires en négociation suffisent.',
            segments: [
              { label: 'Signé', value: 64, max: 100, status: 'good' },
              { label: 'En négociation', value: 28, max: 100, status: 'warning' },
            ],
          }, 'half'),
        ]),
        tab('team', 'Équipe', [
          section('activity', 'Activité par jour', 'detail', 'chart', {
            kind: 'bar',
            categories: ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.'],
            series: [
              { key: 'calls', label: 'Appels', values: [42, 51, 38, 47, 30] },
              { key: 'meetings', label: 'Rendez-vous', values: [6, 8, 5, 9, 3] },
            ],
          }, 'half'),
          section('ranking', 'Signé ce trimestre', 'detail', 'chart', {
            kind: 'horizontal_bar', unit: 'k€',
            categories: ['Camille Durand-Lefèvre', 'Sacha', 'Noa', 'Alex'],
            series: [{ key: 'signed', label: 'Signé', values: [64, 51, 38, 22] }],
            insight: 'Camille a déjà atteint 80 % de son objectif ; Alex a besoin d’un coup de main.',
          }, 'half'),
          section('team-kpis', 'L’équipe cette semaine', 'summary', 'metric_grid', {
            metrics: [
              { label: 'Rendez-vous', value: 31, trend: 'up', trend_value: '+4' },
              { label: 'Pipeline créé', value: '96 000', unit: '€', trend: 'up', trend_value: '+18 %', status: 'good' },
              { label: 'Signées', value: 4 },
            ],
          }),
        ], 'team'),
      ],
    },
  },
];
